import React from "react";
import AnimationRevealPage from "../../../../helpers/AnimationRevealPage.js";
import tw from "twin.macro";
import Header from "../../../../Components/headers/PageHeader";
import MainFeature1 from "../../../../Components/Features/TwoColWithButton.js"; // Reused component
import Features from "../../../../Components/Features/ThreeColumnPages"; // Reused component
import {
  FaBolt,
  FaMobileAlt,
  FaPaintBrush,
  FaUsers,
  FaChartLine,
  FaMapMarkerAlt,
} from "react-icons/fa";

// Styled Components
const Subheading = tw.span`uppercase tracking-wider text-sm`;
const Heading = tw.h1`text-2xl md:text-4xl lg:text-5xl font-bold`;
const Description = tw.p`text-base md:text-lg lg:text-xl`;

// Modern UI/UX Trends in Al Barsha Page Component
export const ModernUIUXTrendsInAlBarsha = () => {
  return (
    <AnimationRevealPage>
      {/* Header Section */}
      <Header from="Modern UI/UX Trends in Al Barsha" />

      {/* Hero Section */}
      <MainFeature1
        subheading={
          <Subheading>Stay Ahead with Cutting-Edge Design</Subheading>
        }
        heading={
          <Heading>
            Discover the Latest{" "}
            <span tw="text-primary-500">Modern UI/UX Trends in Al Barsha</span>
          </Heading>
        }
        description={
          <Description>
            At MilaajBrandset, we specialize in leveraging modern UI/UX trends
            to help businesses in **Al Barsha**, Dubai, stay ahead of the
            competition. Our team combines creativity, innovation, and technical
            expertise to craft digital experiences that captivate users and
            drive measurable results.
          </Description>
        }
        imageSrcs={[
          "https://images.unsplash.com/photo-1581291518633-83b4ebd1d83e?ixlib=rb-4.0.3&auto=format&fit=crop&q=80", // UI/UX visuals
          "https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?ixlib=rb-4.0.3&auto=format&fit=crop&q=80", // Team collaboration
          "https://images.unsplash.com/photo-1590650046871-92c887180603?ixlib=rb-4.0.3&auto=format&fit=crop&q=80", // Client success brainstorming
        ]}
        widths={["55%", "60%", "70%"]}
        heights={["45%", "60%", "35%"]}
        imageColumnStyle="height: 400px;"
        imageStyle="width: 45%; height: 60%;"
        imagePositions={[
          "left: 35%; top: 20%;",
          "right: 30%; top: 10%;",
          "left: 32%; bottom: 20%;",
        ]}
        primaryButtonText="Explore Trends Today"
        buttonRounded={true}
        textOnLeft={false}
      />

      {/* Key Benefits Section */}
      <Features
        subheading={<Subheading>Why Follow Modern Trends?</Subheading>}
        heading="Leverage the Latest Trends to Elevate Your Brand in Al Barsha"
        description="Adopting modern UI/UX trends ensures your business stays competitive and delivers exceptional user experiences."
        cards={[
          {
            icon: <FaBolt color="#ff5464" size={40} />,
            title: "Microinteractions",
            description:
              "Enhance user engagement with subtle animations and interactive elements.",
          },
          {
            icon: <FaMobileAlt color="#ff5464" size={40} />,
            title: "Mobile-First Design",
            description:
              "Prioritize mobile usability to cater to the growing number of mobile users.",
          },
          {
            icon: <FaPaintBrush color="#ff5464" size={40} />,
            title: "Neumorphism",
            description:
              "Combine flat design with 3D effects for a futuristic and sleek aesthetic.",
          },
          {
            icon: <FaUsers color="#ff5464" size={40} />,
            title: "Personalized Experiences",
            description:
              "Use AI and data analytics to deliver tailored content and interactions.",
          },
          {
            icon: <FaChartLine color="#ff5464" size={40} />,
            title: "Voice User Interfaces",
            description:
              "Integrate voice commands to enhance accessibility and convenience.",
          },
          {
            icon: <FaMapMarkerAlt color="#ff5464" size={40} />,
            title: "Localized Design",
            description:
              "Adapt designs to reflect cultural nuances and preferences in Al Barsha.",
          },
        ]}
      />

      {/* Our Process Section */}
      <MainFeature1
        subheading={<Subheading>How We Implement Trends</Subheading>}
        heading="Our Approach to Modern UI/UX Trends in Al Barsha"
        description={
          <Description>
            Our Al Barsha-focused process ensures quality, efficiency, and
            measurable results:
            <ul tw="mt-4 list-disc pl-6">
              <li>
                Trend Research: Stay updated on the latest global and local
                trends.
              </li>
              <li>
                User Analysis: Understand how these trends align with your
                audience's needs.
              </li>
              <li>
                Design Planning: Incorporate modern trends into wireframes and
                prototypes.
              </li>
              <li>
                Development: Build responsive and trend-aligned designs for your
                platform.
              </li>
              <li>
                User Testing: Gather feedback to refine and optimize the
                designs.
              </li>
              <li>
                Implementation Support: Collaborate with your team to bring the
                designs to life.
              </li>
            </ul>
          </Description>
        }
        imageSrcs={[
          "https://images.unsplash.com/photo-1559028012-481c04fa702d?ixlib=rb-4.0.3&auto=format&fit=crop&q=80", // UI/UX visuals
          "https://images.unsplash.com/photo-1542744173-8e7e53415bb0?ixlib=rb-4.0.3&auto=format&fit=crop&q=80", // Team collaboration
          "https://images.unsplash.com/photo-1565728744382-61accd4aa148?ixlib=rb-4.0.3&auto=format&fit=crop&q=80", // Client success brainstorming
        ]}
        widths={["60%", "55%", "70%"]}
        heights={["60%", "45%", "35%"]}
        imageColumnStyle="height: 400px;"
        imageStyle="width: 45%; height: 60%;"
        imagePositions={[
          "right: 30%; top: 10%;",
          "left: 35%; bottom: 20%;",
          "right: 32%; bottom: 10%;",
        ]}
        primaryButtonText="Learn More"
        buttonRounded={true}
        textOnLeft={true}
      />

      {/* Success Stories Section */}
      <Features
        subheading={<Subheading>Success Stories</Subheading>}
        heading="Results That Speak for Themselves"
        description="Here’s how our adoption of modern UI/UX trends has empowered businesses in Al Barsha."
        cards={[
          {
            icon: <FaBolt color="#ff5464" size={40} />,
            title: "Increased Engagement",
            description:
              "Helped a local startup in Al Barsha achieve a 150% increase in user engagement through microinteractions.",
          },
          {
            icon: <FaMobileAlt color="#ff5464" size={40} />,
            title: "Mobile-First Success",
            description:
              "Improved mobile user retention by 200% using responsive and mobile-first design principles.",
          },
          {
            icon: <FaChartLine color="#ff5464" size={40} />,
            title: "Data-Driven Results",
            description:
              "Achieved a 180% increase in conversions using personalized user experiences powered by AI.",
          },
        ]}
      />

      {/* Call-to-Action Section */}
      <MainFeature1
        subheading={<Subheading>Ready to Adopt Modern Trends?</Subheading>}
        heading="Let’s Create Impactful Solutions Together in Al Barsha"
        description={
          <Description>
            Partner with the leading provider of modern UI/UX trends in Al
            Barsha to unlock your brand’s full potential. Whether you’re
            launching a website, mobile app, or software, we’re here to help.
          </Description>
        }
        imageSrcs={[
          "https://images.unsplash.com/photo-1553877522-43269d4ea984?ixlib=rb-4.0.3&auto=format&fit=crop&q=80", // Team celebrating success
          "https://images.unsplash.com/photo-1512453979798-5ea266f8880c?ixlib=rb-4.0.3&auto=format&fit=crop&q=80", // Dubai skyline with tech elements
          "https://images.unsplash.com/photo-1577563908411-5077b6dc7624?ixlib=rb-4.0.3&auto=format&fit=crop&q=80", // Partnership concept
        ]}
        widths={["55%", "60%", "70%"]}
        heights={["45%", "60%", "35%"]}
        imageColumnStyle="height: 400px;"
        imageStyle="width: 45%; height: 60%;"
        imagePositions={[
          "left: 35%; top: 20%;",
          "right: 30%; top: 10%;",
          "left: 32%; bottom: 20%;",
        ]}
        primaryButtonText="Contact Us Today"
        buttonRounded={true}
        textOnLeft={false}
      />
    </AnimationRevealPage>
  );
};
